import {
  EndpointBuilder,
  QueryDefinition,
  BaseQueryFn,
  MutationDefinition,
  InfiniteQueryDefinition,
} from '@reduxjs/toolkit/query';

import type { IProjectMission } from 'interfaces/ProjectMission.interface';
import type {
  ICollection,
  ICollectionApiResponse,
  IInfinitePaginationArgs,
} from 'services/api/api.types';
import {
  serializeFilters,
  serializeFlatObject,
} from 'services/helper/getPaginationParams.helper';
import type {
  IGetMissionCollectionArgs,
  IGetMissionArgs,
  IPostMissionArgs,
} from 'services/projectMissions/projectMissions.api.types';
import { transformCollectionResponse } from 'services/teamHeroApi.helpers';

export const getProjectMissions = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): QueryDefinition<
  IGetMissionCollectionArgs,
  BaseQueryFn,
  string,
  ICollection<IProjectMission>
> =>
  build.query<ICollection<IProjectMission>, IGetMissionCollectionArgs>({
    providesTags: ['ProjectMission'],
    query: (queryArg: IGetMissionCollectionArgs) => {
      const paginationParamObject = {
        page: queryArg.page,
        itemsPerPage: queryArg.itemsPerPage,
        pagination: queryArg.pagination,
        ...queryArg.order,
      };
      const paginationQuery = serializeFlatObject(paginationParamObject, '?');
      const filterQuery = serializeFilters(queryArg.filters);
      return {
        url: `/api/project_missions${paginationQuery}${filterQuery}`,
      };
    },
    transformResponse: (
      response: ICollectionApiResponse<IProjectMission>
    ): ICollection<IProjectMission> =>
      transformCollectionResponse<IProjectMission>(response),
  });

export const getInfiniteProjectMissions = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): InfiniteQueryDefinition<
  IGetMissionCollectionArgs,
  IInfinitePaginationArgs,
  BaseQueryFn,
  string,
  ICollection<IProjectMission>
> =>
  build.infiniteQuery<
    ICollection<IProjectMission>,
    IGetMissionCollectionArgs,
    IInfinitePaginationArgs
  >({
    providesTags: ['ProjectMission'],
    infiniteQueryOptions: {
      // Must provide a default initial page param value
      initialPageParam: {
        page: 1,
        itemsPerPage: 10,
      },
      getNextPageParam: (
        lastPage,
        _allPages,
        { itemsPerPage, page: pageLastPage },
        _allPageParams
      ) => {
        const totalCount = lastPage?.totalCount;
        if (!totalCount || !itemsPerPage) {
          return undefined;
        }
        if (totalCount <= itemsPerPage * pageLastPage || totalCount === 0) {
          return undefined;
        }
        return { page: pageLastPage + 1, itemsPerPage };
      },
      getPreviousPageParam: (
        _firstPage,
        _allPages,
        { page: pageFirstPage, itemsPerPage },
        _allPageParams
      ) => {
        const prevPage = pageFirstPage - 1;
        if (prevPage < 1) {
          return undefined;
        }
        return { page: prevPage, itemsPerPage };
      },
    },
    query: ({ queryArg, pageParam: { page, itemsPerPage } }) => {
      const paginationParamObject = {
        page,
        itemsPerPage,
        pagination: queryArg.pagination,
        ...queryArg.order,
      };
      const paginationQuery = serializeFlatObject(paginationParamObject, '?');
      const filterQuery = serializeFilters(queryArg.filters);
      return `api/project_missions${paginationQuery}${filterQuery}`;
    },
    transformResponse: (
      response: ICollectionApiResponse<IProjectMission>
    ): ICollection<IProjectMission> =>
      transformCollectionResponse<IProjectMission>(response),
  });

export const getProjectMissionItem = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): QueryDefinition<IGetMissionArgs, BaseQueryFn, string, IProjectMission> => {
  return build.query<IProjectMission, IGetMissionArgs>({
    providesTags: ['ProjectMission'],
    query: (queryArg: IGetMissionArgs) => ({
      url: `/api/project_missions/${queryArg.id}`,
    }),
  });
};

export const postProjectMissionItem = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<IPostMissionArgs, BaseQueryFn, string, IProjectMission> =>
  build.mutation<IProjectMission, IPostMissionArgs>({
    invalidatesTags: (_result, _error, args) =>
      args.skipInvalidateTags ? [] : ['ProjectMission'],
    query: (queryArg) => ({
      url: `api/project_missions`,
      method: 'POST',
      body: queryArg.body,
    }),
  });
